import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { postNotification } from "../../../MyApi/notifications";
import { getUsers } from "../../../MyApi/user";
import Select from "react-select";


const NotificationModal = ({ isOpen, closeModal, refetch }) => {
  const [formData, setFormData] = useState({
    translations: {
      en: {
        title: "",
        body: ""
      },
      ar: {
        title: "",
        body: ""
      }
    },
    data: {
      key1: "",
      key2: ""
    },
    target_type: "all_users",
    target_data: {
      user_ids: []
    },
    top_users_count: "",
    performance_metric: "order_count",
    is_scheduled: false,
    scheduled_at: ""
  });

  // Fetch users for specific users targeting
  const { data: usersData, isLoading: usersLoading } = useQuery(
    ['users', 'all'],
    () => getUsers(0),
    {
      enabled: isOpen && formData.target_type === "specific_users",
      keepPreviousData: true,
    }
  );

  const users = usersData?.data?.users || [];

  const [errors, setErrors] = useState({});

  const mutation = useMutation({
    mutationKey: ["postNotification"],
    mutationFn: (postData) => postNotification(postData),
    onSuccess: (data) => {
      toast.success(`${data.message}`);
      setFormData({
        translations: {
          en: { title: "", body: "" },
          ar: { title: "", body: "" }
        },
        data: { key1: "", key2: "" },
        target_type: "all_users",
        target_data: { user_ids: [] },
        top_users_count: "",
        performance_metric: "order_count",
        is_scheduled: false,
        scheduled_at: ""
      });
      setErrors({});
      refetch();
      closeModal();
    },
    onError: (error) => {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(`${error.response?.data?.message || "حدث خطأ ما"}`);
      }
    }
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleTranslationChange = (lang, field, value) => {
    setFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value
        }
      }
    }));
  };

  const handleDataChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [key]: value
      }
    }));
  };

  const handleTargetDataChange = (value) => {
    try {
      const user_ids = value.split(',').map(id => id.trim()).filter(id => id);
      setFormData(prev => ({
        ...prev,
        target_data: {
          user_ids: user_ids
        }
      }));
    } catch (error) {
      // Handle invalid input
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.translations.en.title.trim()) {
      newErrors.title = "عنوان الإشعار باللغة الإنجليزية مطلوب";
    }
    if (!formData.translations.en.body.trim()) {
      newErrors.body = "نص الإشعار باللغة الإنجليزية مطلوب";
    }
    
    if (formData.target_type === "specific_users" && formData.target_data.user_ids.length === 0) {
      newErrors.user_ids = "يجب تحديد معرفات المستخدمين";
    }
    
    if (formData.is_scheduled && !formData.scheduled_at) {
      newErrors.scheduled_at = "تاريخ الجدولة مطلوب";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const postData = {
      ...formData,
      target_data: formData.target_type === "specific_users" ? formData.target_data : undefined,
      top_users_count: formData.target_type === "top_users" ? parseInt(formData.top_users_count) : undefined,
      scheduled_at: formData.is_scheduled ? formData.scheduled_at : undefined
    };

    mutation.mutate(postData);
  };

  return (
    <Modal isOpen={isOpen} toggle={closeModal} size="lg">
      <ModalHeader toggle={closeModal}>
        إرسال إشعار جديد
      </ModalHeader>
      <ModalBody>
        <div className="row add">
          {/* English Translations */}
          <div className="col-12 mb-3">
            <h6>الترجمة الإنجليزية</h6>
          </div>
          
          <div className="col-6">
            <label className="form-label">العنوان</label>
            <input
              type="text"
              className={`form-control ${errors.title ? 'is-invalid' : ''}`}
              value={formData.translations.en.title}
              onChange={(e) => handleTranslationChange('en', 'title', e.target.value)}
            />
            {errors.title && <div className="invalid-feedback">{errors.title}</div>}
          </div>

          <div className="col-6">
            <label className="form-label">النص</label>
            <input
              type="text"
              className={`form-control ${errors.body ? 'is-invalid' : ''}`}
              value={formData.translations.en.body}
              onChange={(e) => handleTranslationChange('en', 'body', e.target.value)}
            />
            {errors.body && <div className="invalid-feedback">{errors.body}</div>}
          </div>

          {/* Arabic Translations */}
          <div className="col-12 mb-3 mt-4">
            <h6>الترجمة العربية</h6>
          </div>
          
          <div className="col-6">
            <label className="form-label">العنوان</label>
            <input
              type="text"
              className="form-control"
              value={formData.translations.ar.title}
              onChange={(e) => handleTranslationChange('ar', 'title', e.target.value)}
            />
          </div>

          <div className="col-6">
            <label className="form-label">النص</label>
            <input
              type="text"
              className="form-control"
              value={formData.translations.ar.body}
              onChange={(e) => handleTranslationChange('ar', 'body', e.target.value)}
            />
          </div>

          {/* Data Section */}
          <div className="col-12 mb-3 mt-4">
            <h6>البيانات الإضافية</h6>
          </div>
          
          <div className="col-6">
            <label className="form-label">المفتاح 1</label>
            <input
              type="text"
              className="form-control"
              value={formData.data.key1}
              onChange={(e) => handleDataChange('key1', e.target.value)}
            />
          </div>

          <div className="col-6">
            <label className="form-label">القيمة 1</label>
            <input
              type="text"
              className="form-control"
              value={formData.data.value1 || ""}
              onChange={(e) => handleDataChange('value1', e.target.value)}
            />
          </div>

          <div className="col-6 mt-3">
            <label className="form-label">المفتاح 2</label>
            <input
              type="text"
              className="form-control"
              value={formData.data.key2}
              onChange={(e) => handleDataChange('key2', e.target.value)}
            />
          </div>

          <div className="col-6 mt-3">
            <label className="form-label">القيمة 2</label>
            <input
              type="text"
              className="form-control"
              value={formData.data.value2 || ""}
              onChange={(e) => handleDataChange('value2', e.target.value)}
            />
          </div>

          {/* Target Type */}
          <div className="col-12 mb-3 mt-4">
            <h6>نوع المستهدفين</h6>
          </div>
          
          <div className="col-12">
            <select
              className="form-select"
              value={formData.target_type}
              onChange={(e) => handleChange('target_type', e.target.value)}
            >
              <option value="all_users">جميع المستخدمين</option>
              <option value="specific_users">مستخدمين محددين</option>
              <option value="top_users">أفضل المستخدمين</option>
            </select>
          </div>

          {/* Target Data for Specific Users */}
          {formData.target_type === "specific_users" && (
            <div className="col-12 mt-3">
              <label className="form-label">اختيار المستخدمين</label>
              {usersLoading ? (
                <div className="form-control">جارٍ تحميل المستخدمين...</div>
              ) : (
                <Select
                  isMulti
                  options={users.map(user => ({
                    value: user.id,
                    label: `${user.name} (${user.phone}) (${user.phone_code})`
                  }))}
                  value={users
                    .filter(user => formData.target_data.user_ids.includes(user.id))
                    .map(user => ({
                      value: user.id,
                      label: `${user.name} (${user.phone}) (${user.phone_code})`
                    }))}
                  onChange={(selectedOptions) => {
                    const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                    setFormData(prev => ({
                      ...prev,
                      target_data: {
                        user_ids: selectedIds
                      }
                    }));
                  }}
                  placeholder="اختر المستخدمين..."
                  noOptionsMessage={() => "لا توجد مستخدمين متاحين"}
                  loadingMessage={() => "جارٍ التحميل..."}
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
              )}
              {errors.user_ids && <div className="text-danger mt-2">{errors.user_ids}</div>}
            </div>
          )}

          {/* Top Users Configuration */}
          {formData.target_type === "top_users" && (
            <>
              <div className="col-6 mt-3">
                <label className="form-label">عدد أفضل المستخدمين</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.top_users_count}
                  onChange={(e) => handleChange('top_users_count', e.target.value)}
                />
              </div>

              <div className="col-6 mt-3">
                <label className="form-label">مقياس الأداء</label>
                <select
                  className="form-select"
                  value={formData.performance_metric}
                  onChange={(e) => handleChange('performance_metric', e.target.value)}
                >
                  <option value="order_count">عدد الطلبات</option>
                  <option value="total_spent">إجمالي المبلغ المنفق</option>
                  <option value="last_order_date">تاريخ آخر طلب</option>
                </select>
              </div>
            </>
          )}

          {/* Scheduling */}
          <div className="col-12 mb-3 mt-4">
            <h6>الجدولة</h6>
          </div>
          
          <div className="col-12">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={formData.is_scheduled}
                onChange={(e) => handleChange('is_scheduled', e.target.checked)}
              />
              <label className="form-check-label">جدولة الإشعار</label>
            </div>
          </div>

          {formData.is_scheduled && (
            <div className="col-12 mt-3">
              <label className="form-label">تاريخ ووقت الجدولة</label>
              <input
                type="datetime-local"
                className={`form-control ${errors.scheduled_at ? 'is-invalid' : ''}`}
                value={formData.scheduled_at}
                onChange={(e) => handleChange('scheduled_at', e.target.value)}
              />
              {errors.scheduled_at && <div className="invalid-feedback">{errors.scheduled_at}</div>}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="d-flex justify-content-center gap-3">
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleSubmit}
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? "جاري الإرسال..." : "إرسال الإشعار"}
          </button>

          <button type="button" className="btn btn-white" onClick={closeModal}>
            إلغاء
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default NotificationModal;